import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { FormattedMessage, useIntl } from 'react-intl';
import 'react-datepicker/dist/react-datepicker.css';
import './MikaDateFilter.scss';

export interface DateRange {
    from: string;
    to: string;
    label?: string;
}

interface FilterByOption {
    value: string;
    label: string;
}

interface MikaDateFilterProps {
    value: DateRange | null;
    onChange: (range: DateRange | null) => void;
    filterBy?: string;
    onFilterByChange?: (value: string) => void;
    filterByOptions?: FilterByOption[];
}

const toDateStr = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const toDate = (s: string): Date | null => s ? new Date(s + 'T00:00:00') : null;

const getToday = () => toDateStr(new Date());
const getYesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return toDateStr(d); };
const getWeekStart = () => { const d = new Date(); const day = d.getDay(); d.setDate(d.getDate() - day + (day === 0 ? -6 : 1)); return toDateStr(d); };
const getMonthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const getLastMonthRange = (): [string, string] => { const d = new Date(); d.setMonth(d.getMonth() - 1); const from = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; const last = new Date(d.getFullYear(), d.getMonth() + 1, 0); return [from, toDateStr(last)]; };
const getLast3MonthsStart = () => { const d = new Date(); d.setMonth(d.getMonth() - 3); return toDateStr(d); };
const getYearStart = () => `${new Date().getFullYear()}-01-01`;

const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

// Calendar header — months derived from Intl.DateTimeFormat so new locales get
// free translation without a hand-curated month list.
interface CalHeaderProps {
    date: Date; changeYear: (y: number) => void; changeMonth: (m: number) => void;
    decreaseMonth: () => void; increaseMonth: () => void;
    prevMonthButtonDisabled: boolean; nextMonthButtonDisabled: boolean;
    months: string[];
}

const CalHeader: React.FC<CalHeaderProps> = ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, months }) => {
    const [showMonths, setShowMonths] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);
    const year = date.getFullYear();
    const month = date.getMonth();

    useEffect(() => {
        const cal = headerRef.current?.closest('.react-datepicker') as HTMLElement | null;
        if (showMonths) cal?.classList.add('mdf-month-picker-active');
        else cal?.classList.remove('mdf-month-picker-active');
    }, [showMonths]);

    function openMonthPicker() {
        setShowMonths(true);
    }

    if (showMonths) return (
        <div ref={headerRef} className="mdf-cal-header mdf-cal-header--months">
            <div className="mdf-year-nav">
                <button type="button" className="mdf-nav-btn" onClick={() => changeYear(year - 1)}>‹</button>
                <span className="mdf-year-label">{year}</span>
                <button type="button" className="mdf-nav-btn" onClick={() => changeYear(year + 1)}>›</button>
            </div>
            <div className="mdf-month-grid">
                {months.map((m, i) => (
                    <button type="button" key={m}
                        className={`mdf-month-btn${month === i ? ' active' : ''}`}
                        onClick={() => { changeMonth(i); setShowMonths(false); }}>
                        {m}
                    </button>
                ))}
            </div>
        </div>
    );

    return (
        <div ref={headerRef} className="mdf-cal-header">
            <button type="button" className="mdf-nav-btn" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
            <button type="button" className="mdf-month-year-btn" onClick={openMonthPicker}>
                {months[month]} {year} <span className="mdf-chevron">▾</span>
            </button>
            <button type="button" className="mdf-nav-btn" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
        </div>
    );
};

const MikaDateFilter: React.FC<MikaDateFilterProps> = ({ value, onChange, filterBy, onFilterByChange, filterByOptions }) => {
    const intl = useIntl();
    const locale = intl.locale;
    const fm = (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage });

    // Month + weekday names from Intl.DateTimeFormat — locale automatically
    // covered for every supported language (no hand translation needed).
    const months = useMemo(() => {
        try {
            const fmt = new Intl.DateTimeFormat(locale, { month: 'long' });
            return Array.from({ length: 12 }, (_, i) =>
                fmt.format(new Date(2000, i, 1))
            );
        } catch {
            // Fallback if the browser doesn't know the locale.
            return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        }
    }, [locale]);

    const weekdayNarrow = useMemo(() => {
        try {
            const fmt = new Intl.DateTimeFormat(locale, { weekday: 'narrow' });
            // 2000-01-02 is a Sunday — we need Sun..Sat (indices 0..6).
            return Array.from({ length: 7 }, (_, i) =>
                fmt.format(new Date(2000, 0, 2 + i))
            );
        } catch {
            return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
        }
    }, [locale]);

    const presets = useMemo(() => [
        { key: 'today',         label: fm('common.datefilter.preset.today', 'Today'),               getRange: (): DateRange => ({ from: getToday(),            to: getToday(),        label: fm('common.datefilter.preset.today', 'Today') }) },
        { key: 'yesterday',     label: fm('common.datefilter.preset.yesterday', 'Yesterday'),       getRange: (): DateRange => ({ from: getYesterday(),        to: getYesterday(),    label: fm('common.datefilter.preset.yesterday', 'Yesterday') }) },
        { key: 'this_week',     label: fm('common.datefilter.preset.thisWeek', 'This week'),        getRange: (): DateRange => ({ from: getWeekStart(),        to: getToday(),        label: fm('common.datefilter.preset.thisWeek', 'This week') }) },
        { key: 'this_month',    label: fm('common.datefilter.preset.thisMonth', 'This month'),      getRange: (): DateRange => ({ from: getMonthStart(),       to: getToday(),        label: fm('common.datefilter.preset.thisMonth', 'This month') }) },
        { key: 'last_month',    label: fm('common.datefilter.preset.lastMonth', 'Last month'),      getRange: (): DateRange => { const [f, t] = getLastMonthRange(); return { from: f, to: t, label: fm('common.datefilter.preset.lastMonth', 'Last month') }; } },
        { key: 'last_3_months', label: fm('common.datefilter.preset.last3Months', 'Last 3 months'), getRange: (): DateRange => ({ from: getLast3MonthsStart(), to: getToday(),        label: fm('common.datefilter.preset.last3Months', 'Last 3 months') }) },
        { key: 'this_year',     label: fm('common.datefilter.preset.thisYear', 'This year'),        getRange: (): DateRange => ({ from: getYearStart(),        to: getToday(),        label: fm('common.datefilter.preset.thisYear', 'This year') }) },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl]);

    const [open, setOpen] = useState(false);
    const [pickingFrom, setPickingFrom] = useState(true);
    const [tempFrom, setTempFrom] = useState<Date | null>(null);
    const [tempTo, setTempTo] = useState<Date | null>(null);
    // isMobile drives the portal/inline rendering branch below. We track it via state so a
    // viewport resize (e.g. device rotation, DevTools mobile mode toggle) re-renders the
    // dropdown into the right tree. window.matchMedia keeps the breakpoint in one place.
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.matchMedia('(max-width: 576px)').matches);
    const ref = useRef<HTMLDivElement>(null);
    // Separate ref for the dropdown — on mobile it's portaled to document.body, so
    // ref.current.contains() no longer covers it for the click-outside check.
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (value) {
            setTempFrom(toDate(value.from));
            setTempTo(toDate(value.to));
        } else {
            setTempFrom(null);
            setTempTo(null);
        }
    }, [value]);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 576px)');
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        // Older Safari uses addListener/removeListener; modern browsers prefer addEventListener.
        if (mq.addEventListener) mq.addEventListener('change', handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handler);
            else mq.removeListener(handler);
        };
    }, []);

    useEffect(() => {
        const handler = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            const insideWrapper = ref.current?.contains(target);
            const insideDropdown = dropdownRef.current?.contains(target);
            if (!insideWrapper && !insideDropdown) setOpen(false);
        };
        if (open) {
            // mousedown handles desktop + most touch-to-mouse synthesis; touchstart is a belt-and-
            // braces capture for older mobile browsers where the synthesis sometimes lags.
            document.addEventListener('mousedown', handler);
            document.addEventListener('touchstart', handler);
        }
        return () => {
            document.removeEventListener('mousedown', handler);
            document.removeEventListener('touchstart', handler);
        };
    }, [open]);

    const handlePreset = (preset: typeof presets[0]) => {
        onChange(preset.getRange());
        setOpen(false);
    };

    const handleDayClick = (date: Date) => {
        if (pickingFrom) {
            setTempFrom(date);
            setTempTo(null);
            setPickingFrom(false);
        } else {
            if (tempFrom && date < tempFrom) {
                // Clicked before start — restart
                setTempFrom(date);
                setTempTo(null);
            } else {
                setTempTo(date);
                setPickingFrom(true);
                if (tempFrom) {
                    onChange({ from: toDateStr(tempFrom), to: toDateStr(date) });
                    setOpen(false);
                }
            }
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
        setTempFrom(null);
        setTempTo(null);
        setPickingFrom(true);
    };

    const displayText = value
        ? (value.label || `${formatDisplayDate(value.from)} – ${formatDisplayDate(value.to)}`)
        : null;

    const activePresetKey = value ? presets.find(p => {
        const r = p.getRange();
        return r.from === value.from && r.to === value.to;
    })?.key : null;

    // The dropdown is portaled to document.body on mobile to escape the parent toolbar's
    // stacking context. The toolbar applies `mask-image` (and on some pages also `transform`
    // or `filter`) which makes `position: fixed` resolve against the toolbar's box rather
    // than the viewport — without the portal the dropdown rendered as a tiny clipped sliver
    // near the top of the page and looked like it wasn't opening at all.
    const dropdownEl = open ? (
        <div className="mdf-dropdown" ref={dropdownRef}>
                    <div className="mdf-left">
                        {filterByOptions && filterByOptions.length > 0 && onFilterByChange && (
                            <>
                                <div className="mdf-section-label">
                                    <FormattedMessage id="common.datefilter.filterBy" defaultMessage="Filter by" />
                                </div>
                                {filterByOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`mdf-preset ${filterBy === opt.value ? 'mdf-preset-active' : ''}`}
                                        onClick={() => onFilterByChange(opt.value)}
                                        type="button"
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                                <div className="mdf-left-divider" />
                            </>
                        )}
                        <div className="mdf-section-label">
                            <FormattedMessage id="common.datefilter.filterQuick" defaultMessage="Quick filter" />
                        </div>
                        {presets.map(preset => (
                            <button
                                key={preset.key}
                                className={`mdf-preset ${activePresetKey === preset.key ? 'mdf-preset-active' : ''}`}
                                onClick={() => handlePreset(preset)}
                                type="button"
                            >
                                {preset.label}
                            </button>
                        ))}
                    </div>
                    <div className="mdf-right">
                        <div className="mdf-range-label">
                            {!pickingFrom && tempFrom ? (
                                <span><FormattedMessage id="common.datefilter.selectEndDate" defaultMessage="Select end date" /></span>
                            ) : (
                                <span><FormattedMessage id="common.datefilter.selectStartDate" defaultMessage="Select start date" /></span>
                            )}
                        </div>
                        <div className="mdf-calendar-container">
                            <DatePicker
                                selected={pickingFrom ? tempFrom : tempTo}
                                onChange={handleDayClick}
                                startDate={tempFrom}
                                endDate={tempTo}
                                selectsRange={false}
                                inline
                                calendarClassName="mdf-calendar"
                                renderCustomHeader={(p: any) => <CalHeader {...p} months={months} />}
                                formatWeekDay={(nameOfDay) => weekdayNarrow[['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].indexOf(nameOfDay)]}
                                dayClassName={(date) => {
                                    if (tempFrom && tempTo) {
                                        if (date >= tempFrom && date <= tempTo) return 'mdf-in-range';
                                    }
                                    return '';
                                }}
                            />
                        </div>
                        {tempFrom && (
                            <div className="mdf-range-summary">
                                <button
                                    type="button"
                                    className={`mdf-range-date ${pickingFrom ? 'mdf-range-date-active' : ''}`}
                                    onClick={() => { setPickingFrom(true); setTempFrom(null); setTempTo(null); }}
                                >
                                    {formatDisplayDate(toDateStr(tempFrom))}
                                </button>
                                <span className="mdf-range-arrow">→</span>
                                <button
                                    type="button"
                                    className={`mdf-range-date ${!pickingFrom ? 'mdf-range-date-active' : ''}`}
                                    onClick={() => { if (tempTo) { setPickingFrom(false); setTempTo(null); } }}
                                >
                                    {tempTo ? formatDisplayDate(toDateStr(tempTo)) : '...'}
                                </button>
                            </div>
                        )}
                    </div>
        </div>
    ) : null;

    return (
        <div className="mdf-wrapper" ref={ref}>
            <button
                className={`mdf-trigger ${value ? 'mdf-trigger-active' : ''}`}
                onClick={() => { setOpen(!open); setPickingFrom(true); }}
                type="button"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>{displayText || fm('common.datefilter.placeholder', 'Date')}</span>
                {value ? (
                    <span className="mdf-clear" onClick={handleClear}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </span>
                ) : (
                    <svg className="mdf-chevron" width="8" height="5" viewBox="0 0 8 5" fill="none" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
                        <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </button>

            {/* Desktop renders the dropdown in-tree so position:absolute anchors to the wrapper.
                Mobile portals to document.body so position:fixed resolves against the viewport
                instead of the toolbar's mask/transform stacking context. */}
            {isMobile && dropdownEl ? createPortal(dropdownEl, document.body) : dropdownEl}
        </div>
    );
};

export default MikaDateFilter;
